import { useEffect, useMemo, useRef, useState } from "react";
import { fileUrl } from "@/v2/lib/files";

let youtubeApiPromise;

export function getYouTubeId(value = "") {
  const source = String(value).trim();
  if (!source) return "";
  try {
    const url = new URL(/^https?:\/\//i.test(source) ? source : `https://${source}`);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return url.pathname.split("/").filter(Boolean)[0] || "";
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const direct = url.searchParams.get("v");
      if (direct) return direct;
      const parts = url.pathname.split("/").filter(Boolean);
      if (["embed", "shorts", "live"].includes(parts[0])) return parts[1] || "";
    }
  } catch {
    // A plain video ID is also accepted for easier administration.
  }
  return /^[A-Za-z0-9_-]{11}$/.test(source) ? source : "";
}

function loadYouTubeApi() {
  if (typeof window === "undefined") return Promise.reject(new Error("browser-only"));
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve, reject) => {
    const previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve(window.YT);
    };
    const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existing) return;
    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.onerror = () => reject(new Error("youtube-api-unavailable"));
    document.head.appendChild(script);
  });
  return youtubeApiPromise;
}

function YouTubeVideo({ id, title, onProgress, onError, testId }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const timerRef = useRef(null);
  const progressRef = useRef(onProgress);
  const errorRef = useRef(onError);

  useEffect(() => { progressRef.current = onProgress; }, [onProgress]);
  useEffect(() => { errorRef.current = onError; }, [onError]);

  useEffect(() => {
    let cancelled = false;
    const report = () => {
      const player = playerRef.current;
      const duration = Number(player?.getDuration?.() || 0);
      const current = Number(player?.getCurrentTime?.() || 0);
      if (duration > 0) progressRef.current?.(Math.min(100, Math.round((current / duration) * 100)));
    };

    loadYouTubeApi().then((YT) => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: id,
        width: "100%",
        height: "100%",
        playerVars: { rel: 0, modestbranding: 1, playsinline: 1, origin: window.location.origin },
        events: {
          onReady: report,
          onStateChange: (event) => {
            window.clearInterval(timerRef.current);
            if (event.data === YT.PlayerState.PLAYING) timerRef.current = window.setInterval(report, 1500);
            if (event.data === YT.PlayerState.PAUSED) report();
            if (event.data === YT.PlayerState.ENDED) progressRef.current?.(100);
          },
          onError: () => errorRef.current?.(),
        },
      });
    }).catch(() => errorRef.current?.());

    return () => {
      cancelled = true;
      window.clearInterval(timerRef.current);
      try { playerRef.current?.destroy?.(); } catch { /* iframe was already removed */ }
    };
  }, [id]);

  return <div ref={hostRef} title={title} data-testid={testId} className="w-full aspect-video" />;
}

function NativePlaylist({ items, chapters, title, onProgress, onError, testId }) {
  const videoRef = useRef(null);
  const pendingSeek = useRef(null);
  const pendingPlay = useRef(false);
  const [partIndex, setPartIndex] = useState(0);
  const [measured, setMeasured] = useState({});
  const playlistKey = items.map((item) => `${item.url}|${item.duration_seconds || 0}`).join("\u0001");

  useEffect(() => {
    setPartIndex(0);
    setMeasured({});
    pendingSeek.current = null;
    pendingPlay.current = false;
  }, [playlistKey]);

  const durations = items.map((item, index) => Number(measured[index] || item.duration_seconds || 0));
  const before = (index) => durations.slice(0, index).reduce((sum, value) => sum + value, 0);
  const total = durations.reduce((sum, value) => sum + value, 0);
  const current = items[partIndex] || items[0];

  const report = (video) => {
    const currentDuration = Number(video.duration || durations[partIndex] || 0);
    const knownTotal = total || currentDuration * items.length;
    if (!currentDuration || !knownTotal) return;
    const elapsed = total
      ? before(partIndex) + Math.min(video.currentTime, currentDuration)
      : (partIndex * currentDuration) + Math.min(video.currentTime, currentDuration);
    onProgress?.(Math.min(100, Math.max(0, Math.round((elapsed / knownTotal) * 100))));
  };

  const loaded = (event) => {
    const video = event.currentTarget;
    if (Number.isFinite(video.duration) && video.duration > 0) {
      setMeasured((known) => ({ ...known, [partIndex]: video.duration }));
    }
    if (pendingSeek.current !== null) {
      video.currentTime = Math.min(pendingSeek.current, Math.max(0, video.duration - 0.25));
      pendingSeek.current = null;
    }
    if (pendingPlay.current) {
      pendingPlay.current = false;
      video.play().catch(() => {});
    }
    report(video);
  };

  const selectPart = (index, localSeconds = 0, autoplay = false) => {
    const safeIndex = Math.max(0, Math.min(items.length - 1, index));
    pendingSeek.current = Math.max(0, localSeconds);
    if (safeIndex === partIndex && videoRef.current) {
      videoRef.current.currentTime = pendingSeek.current;
      pendingSeek.current = null;
      if (autoplay) videoRef.current.play().catch(() => {});
      report(videoRef.current);
      return;
    }
    pendingPlay.current = autoplay;
    setPartIndex(safeIndex);
  };

  const ended = () => {
    if (partIndex < items.length - 1) selectPart(partIndex + 1, 0, true);
    else onProgress?.(100);
  };

  const goToChapter = (chapter) => {
    const targetPart = Math.max(0, Math.min(items.length - 1, Number(chapter.part_index || 0)));
    const localSeconds = Math.max(0, Number(chapter.start_seconds || 0) - before(targetPart));
    selectPart(targetPart, localSeconds, true);
  };

  return (
    <div className="bg-[#25343f]" dir="rtl">
      <video
        key={current.url}
        ref={videoRef}
        src={fileUrl(current.url)}
        title={current.title || title}
        controls
        playsInline
        preload="metadata"
        onTimeUpdate={(event) => report(event.currentTarget)}
        onLoadedMetadata={loaded}
        onEnded={ended}
        onError={onError}
        data-testid={testId}
        className="w-full aspect-video bg-black"
      />

      {items.length > 1 && (
        <div className="border-t border-white/10 p-3 sm:p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-[11px] text-[#76C9AD]">المقطع {partIndex + 1} من {items.length}</div>
              <div className="text-sm font-bold text-white truncate">{current.title || title}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={() => selectPart(partIndex - 1)} disabled={partIndex === 0}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white disabled:opacity-30">السابق</button>
              <button type="button" onClick={() => selectPart(partIndex + 1)} disabled={partIndex === items.length - 1}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white disabled:opacity-30">التالي</button>
            </div>
          </div>

          {chapters.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`${testId}-chapters`}>
              {chapters.map((chapter, index) => (
                <button key={`${chapter.part_index}-${chapter.start_seconds}-${index}`} type="button"
                  onClick={() => goToChapter(chapter)}
                  className={`shrink-0 px-3 py-2 rounded-xl border text-xs transition ${Number(chapter.part_index || 0) === partIndex ? "bg-[#91cab8]/15 border-[#91cab8]/35 text-[#7BE0BD]" : "bg-white/5 border-white/10 text-[#ccd6dc] hover:text-white"}`}>
                  {chapter.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SmartVideo({
  src,
  playlist = [],
  chapters = [],
  title = "فيديو تدريبي",
  onProgress,
  onError,
  testId = "smart-video",
  className = "",
}) {
  const items = useMemo(() => {
    const configured = (Array.isArray(playlist) ? playlist : [])
      .filter((item) => item && item.url)
      .map((item, index) => ({
        title: item.title || `المقطع ${index + 1}`,
        url: item.url,
        duration_seconds: Number(item.duration_seconds || 0),
        part_index: index,
      }));
    if (configured.length) return configured;
    return src ? [{ title, url: src, duration_seconds: 0, part_index: 0 }] : [];
  }, [playlist, src, title]);

  const chapterItems = useMemo(() => (Array.isArray(chapters) ? chapters : [])
    .filter((chapter) => chapter && chapter.title), [chapters]);
  const youtubeId = items.length === 1 ? getYouTubeId(items[0]?.url) : "";

  if (!items.length) return null;
  return (
    <div className={`overflow-hidden rounded-2xl bg-black ${className}`}>
      {youtubeId ? (
        <YouTubeVideo id={youtubeId} title={title} onProgress={onProgress} onError={onError} testId={testId} />
      ) : (
        <NativePlaylist items={items} chapters={chapterItems} title={title}
          onProgress={onProgress} onError={onError} testId={testId} />
      )}
    </div>
  );
}
